import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Mail, Lock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [step, setStep] = useState(token ? "reset" : "request"); // "request", "reset", "success"
  const [loading, setLoading] = useState(false);
  
  // Request form
  const [email, setEmail] = useState("");
  
  // Reset form
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Inserisci la tua email");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Email inviata! Controlla la tua casella di posta.");
      setStep("success");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Compila tutti i campi");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      toast.success("Password reimpostata con successo!");
      setStep("success");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset della password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center text-[#A1A1AA] hover:text-[#D4AF37] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Login
          </button>

          <h1 className="text-3xl font-serif text-[#EDEDED] mb-2">
            {step === "request" && "Recupera Password"}
            {step === "reset" && "Nuova Password"}
            {step === "success" && "Fatto!"}
          </h1>
          <p className="text-[#A1A1AA] mb-8">
            {step === "request" && "Inserisci la tua email per ricevere il link di reset"}
            {step === "reset" && "Inserisci la tua nuova password"}
            {step === "success" && token ? "La tua password è stata reimpostata" : "Controlla la tua email"}
          </p>

          {/* Request Reset Form */}
          {step === "request" && (
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[#52525B] uppercase tracking-wider text-xs">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                  <Input
                    type="email"
                    placeholder="nome@esempio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-transparent border-neutral-800 text-[#EDEDED] h-12 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    data-testid="reset-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] h-12 font-medium uppercase tracking-widest text-xs"
                data-testid="reset-request-btn"
              >
                {loading ? "Invio in corso..." : "Invia Link di Reset"}
              </Button>
            </form>
          )}

          {/* Reset Password Form */}
          {step === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[#52525B] uppercase tracking-wider text-xs">
                  Nuova Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                  <Input
                    type="password"
                    placeholder="Minimo 6 caratteri"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 bg-transparent border-neutral-800 text-[#EDEDED] h-12 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#52525B] uppercase tracking-wider text-xs">
                  Conferma Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                  <Input
                    type="password"
                    placeholder="Ripeti la password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-transparent border-neutral-800 text-[#EDEDED] h-12 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] h-12 font-medium uppercase tracking-widest text-xs"
                data-testid="reset-confirm-btn"
              >
                {loading ? "Salvataggio..." : "Reimposta Password"}
              </Button>
            </form>
          )}

          {/* Success State */}
          {step === "success" && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
              {token ? (
                <p className="text-[#A1A1AA] mb-6">
                  Puoi ora accedere con la tua nuova password.
                </p>
              ) : (
                <p className="text-[#A1A1AA] mb-6">
                  Se l'email esiste nel nostro sistema, riceverai un link per reimpostare la password.
                </p>
              )}
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] h-12 font-medium uppercase tracking-widest text-xs px-8"
                data-testid="go-to-login-btn"
              >
                Vai al Login
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-[#0A0A0A] to-[#050505] border-l border-neutral-800">
        <div className="text-center px-12">
          <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <Lock className="w-12 h-12 text-[#D4AF37]" />
          </div>
          <blockquote className="text-2xl font-serif text-[#EDEDED] mb-4">
            "La sicurezza dei tuoi dati è la nostra priorità"
          </blockquote>
          <p className="text-[#52525B] uppercase tracking-[0.3em] text-sm">
            Gestione Documentale Professionale
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
